import type { History, Location } from "history";
import { getProductStatusValue } from "../components/Status/Status";
import { TFunction } from "i18next";
import { AdvancedFilterCard, IFilters } from "../types/types";
import {
  chipsToStringArray,
  compileNumericFilterQueryParams,
  decodeNumericFilterCriterion,
  expandNumericFilterParams,
} from "../components/Filters/filterutils";
import {
  ADVANCED_FILTERS_KEY,
  compileAdvancedFilterCardsForRequest,
  decodeAdvancedFilterCards,
} from "../components/AdvancedFilters/advancedFiltersUtils";

export type ApplyFiltersToURLParams = {
  active_tag_view?: string | null;
  q?: string;
  params: URLSearchParams;
  filtersFromUrl?: { [key: string]: string[] };
};

/**
 * Appends a filter-state map onto a URLSearchParams instance — the shared
 * compile step for every surface that queries via plain GET query params
 * (MDM's EntityListPage.tsx among them; see
 * docs/plans/NUMERIC_FILTERS_FE_SPEC.md §5.2, "MDM listings are GET/query-
 * params only — no POST-body filter form").
 *
 * AP-1016 (Advanced Filters, Part 1): numeric filters are expanded to their
 * compiled `<key>_op`/`<key>_value`/`<key>_min`/`<key>_max` params by
 * `expandNumericFilterParams`, which detects them by attempting to decode
 * each single-value entry as a `NumericFilterCriterion` rather than
 * requiring facet-type metadata to be threaded in here — this function has
 * no access to that metadata for every caller (MDM's own listing fetch in
 * particular), and a chip-filter value coincidentally decoding as a numeric
 * criterion is not realistically possible (it would have to be e.g. the
 * literal string `"between:10,20"`).
 *
 * AP-1016 (Advanced Filters, Part 2): `ADVANCED_FILTERS_KEY`'s value is a
 * JSON blob, never a query param — it must always go through the POST body
 * (see `useAlternateRequest.ts`), so it's stripped here before the
 * numeric-expansion pass, both for the GET-only surfaces this function
 * serves (which never carry Part 2 criteria) and for the byte-size
 * estimation `can_use_alternate_request` runs over this same function.
 */
export function applyFiltersToURL({
  active_tag_view,
  q,
  filtersFromUrl,
  params,
}: ApplyFiltersToURLParams): URLSearchParams {
  if (filtersFromUrl) {
    const withoutAdvancedFilters = Object.fromEntries(
      Object.entries(filtersFromUrl).filter(
        ([key]) => key !== ADVANCED_FILTERS_KEY
      )
    );
    Object.entries(expandNumericFilterParams(withoutAdvancedFilters)).forEach(
      ([key, values]) => {
        values.forEach((value) => params.append(key, value));
      }
    );
  }
  if (active_tag_view) {
    params.append("active_tag_view", active_tag_view);
  }
  if (q) {
    params.append("q", q);
  }
  return params;
}

// Maximum URL size before switching to sessionStorage (bytes)
// Conservative limit to avoid Chrome crashes
const URL_SIZE_THRESHOLD = 1500;

// SessionStorage key prefix
const STORAGE_PREFIX = "filter_state_";

// URL param for filter hash
const HASH_PARAM = "_fs";

// TTL for sessionStorage entries (24 hours)
export const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

export type Filters = { [key: string]: string[] };

interface TimestampedMapState {
  entries: Record<string, number>;
}

/**
 * Load a timestamped key→timestamp map from sessionStorage, pruning expired entries.
 * Used by filter-state and list-filter carry-over dismiss tracking.
 */
export function loadTimestampedMap(storageKey: string): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as
      | TimestampedMapState
      | Record<string, number>;
    let entries: Record<string, number>;
    if (parsed && typeof parsed === "object" && "entries" in parsed) {
      entries = (parsed as TimestampedMapState).entries ?? {};
    } else if (parsed && typeof parsed === "object") {
      // Legacy shape: `{ [key]: 1 }` — treat as freshly written.
      entries = {};
      Object.keys(parsed as Record<string, number>).forEach((key) => {
        entries[key] = Date.now();
      });
    } else {
      return {};
    }
    const now = Date.now();
    const kept: Record<string, number> = {};
    Object.entries(entries).forEach(([key, timestamp]) => {
      if (typeof timestamp === "number" && now - timestamp <= STORAGE_TTL_MS) {
        kept[key] = timestamp;
      }
    });
    return kept;
  } catch {
    return {};
  }
}

/** Persist a timestamped key→timestamp map (removes the key when empty). */
export function saveTimestampedMap(
  storageKey: string,
  entries: Record<string, number>
): void {
  if (Object.keys(entries).length === 0) {
    sessionStorage.removeItem(storageKey);
    return;
  }
  const state: TimestampedMapState = { entries };
  sessionStorage.setItem(storageKey, JSON.stringify(state));
}

/**
 * Atomically read, mutate, and write a timestamped map (single parse/stringify).
 */
export function updateTimestampedMap(
  storageKey: string,
  mutate: (entries: Record<string, number>) => void
): Record<string, number> {
  const entries = loadTimestampedMap(storageKey);
  mutate(entries);
  saveTimestampedMap(storageKey, entries);
  return entries;
}

interface StoredFilterState {
  filters: Filters;
  timestamp: number;
}

/**
 * Generate a stable hash from filter object for sessionStorage key
 */
export function generateFilterHash(filters: Filters): string {
  const str = JSON.stringify(filters);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Calculate byte size of filters when encoded in URL
 */
function getFilterSize(filters: Filters): number {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, values]) => {
    values.forEach((value) => {
      params.append(key, value);
    });
  });
  return new TextEncoder().encode(params.toString()).length;
}

/**
 * Parse URL params into filter object, grouping duplicate keys into arrays
 */
export function groupPortfolioParamsByKey(params: URLSearchParams): Filters {
  return [...params.entries()].reduce<Filters>((acc, tuple) => {
    const [key, val] = tuple;
    if (key in acc) {
      acc[key] = [...acc[key], val];
    } else {
      // Skip 'tab' key as it's not a filter
      if (key !== "tab") {
        acc[key] = [val];
      }
    }
    return acc;
  }, {});
}

/**
 * Clean up old sessionStorage filter entries (older than TTL)
 */
function cleanupOldFilters(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith(STORAGE_PREFIX)) {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const state: StoredFilterState = JSON.parse(stored);
          if (now - state.timestamp > STORAGE_TTL_MS) {
            keysToRemove.push(key);
          }
        }
      } catch (e) {
        // Invalid JSON, remove it
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Load filters from URL or sessionStorage
 */
export function loadFilters(location: Location): Filters {
  const params = new URLSearchParams(location.search.substring(1));

  // Check if filters are in sessionStorage
  const hashParam = params.get(HASH_PARAM);
  if (hashParam) {
    const storageKey = STORAGE_PREFIX + hashParam;
    const stored = sessionStorage.getItem(storageKey);

    if (stored) {
      try {
        const state: StoredFilterState = JSON.parse(stored);

        // Check if expired
        if (Date.now() - state.timestamp <= STORAGE_TTL_MS) {
          return state.filters;
        } else {
          // Expired, clean up
          sessionStorage.removeItem(storageKey);
        }
      } catch (e) {
        // Invalid JSON, fall through to URL parsing
        sessionStorage.removeItem(storageKey);
      }
    }

    // If we get here, hash was in URL but storage was empty/invalid
    // Fall through to parse any remaining URL params
  }

  // Parse filters from URL params
  return groupPortfolioParamsByKey(params);
}

/**
 * Save filters to URL or sessionStorage based on size
 * Returns true if using sessionStorage, false if using URL, null if no actual filters
 */
export function saveFilters(
  filters: Filters,
  history: History,
  pathname: string,
  preserveParams?: { q?: string; offset?: string; perPage?: string }
): boolean | null {
  // Clean up old entries periodically
  if (Math.random() < 0.1) {
    // 10% chance on each save
    cleanupOldFilters();
  }

  // Check if there are any actual filter values (excluding metadata)
  const hasActualFilters = Object.entries(filters).some(
    ([key, values]) =>
      key !== "active_tag_view" &&
      key !== "q" &&
      key !== "offset" &&
      key !== "perPage" &&
      values &&
      values.length > 0
  );

  // If no actual filters, clear the URL
  if (!hasActualFilters) {
    const params = new URLSearchParams();

    // Preserve non-filter params
    if (preserveParams?.q) params.set("q", preserveParams.q);
    if (preserveParams?.offset) params.set("offset", preserveParams.offset);
    if (preserveParams?.perPage) params.set("perPage", preserveParams.perPage);

    const search = params.toString().replace(/\+/g, "%20");
    history.push({
      pathname,
      search: search ? `?${search}` : "",
    });

    return null;
  }

  const filterSize = getFilterSize(filters);
  const useSessionStorage = filterSize > URL_SIZE_THRESHOLD;

  const params = new URLSearchParams();

  // Preserve non-filter params
  if (preserveParams?.q) params.set("q", preserveParams.q);
  if (preserveParams?.offset) params.set("offset", preserveParams.offset);
  if (preserveParams?.perPage) params.set("perPage", preserveParams.perPage);

  if (useSessionStorage) {
    // Store filters in sessionStorage
    const hash = generateFilterHash(filters);
    const storageKey = STORAGE_PREFIX + hash;

    const state: StoredFilterState = {
      filters,
      timestamp: Date.now(),
    };

    sessionStorage.setItem(storageKey, JSON.stringify(state));
    params.set(HASH_PARAM, hash);
  } else {
    // Store filters in URL
    Object.entries(filters).forEach(([key, values]) => {
      values.forEach((value) => {
        params.append(key, value);
      });
    });
  }

  const search = params.toString().replace(/\+/g, "%20");
  history.push({
    pathname,
    search: search ? `?${search}` : "",
  });

  return useSessionStorage;
}

/**
 * Check if filters would fit in URL without exceeding threshold
 */
export function wouldFitInURL(filters: Filters): boolean {
  return getFilterSize(filters) <= URL_SIZE_THRESHOLD;
}

/**
 * Get current storage method for active filters
 */
export function getStorageMethod(
  location: Location
): "url" | "sessionStorage" | null {
  const params = new URLSearchParams(location.search.substring(1));
  const hashParam = params.get(HASH_PARAM);

  if (hashParam) {
    const storageKey = STORAGE_PREFIX + hashParam;
    if (sessionStorage.getItem(storageKey)) {
      return "sessionStorage";
    }
  }

  // Check if there are any filter params (excluding special params)
  const hasFilters = Array.from(params.keys()).some(
    (key) =>
      key !== "q" &&
      key !== "offset" &&
      key !== "perPage" &&
      key !== "tab" &&
      key !== HASH_PARAM
  );

  return hasFilters ? "url" : null;
}

/**
 * Clear all filter state (URL and sessionStorage)
 */
export function clearFilters(
  history: History,
  pathname: string,
  preserveParams?: { q?: string; offset?: string; perPage?: string }
): void {
  const params = new URLSearchParams();

  // Preserve non-filter params
  if (preserveParams?.q) params.set("q", preserveParams.q);
  if (preserveParams?.offset) params.set("offset", preserveParams.offset);
  if (preserveParams?.perPage) params.set("perPage", preserveParams.perPage);

  const search = params.toString().replace(/\+/g, "%20");
  history.replace({
    pathname,
    search: search ? `?${search}` : "",
  });
}

/**
 * Convert filters to new API format with wildcard and exclude support
 * @param filters - Current filter state (old format)
 * @param availableFilters - Metadata about all available filter options
 * @returns New API format with filters, optional exclude_filters, and
 * optional numeric_query_params (AP-1016 — per
 * docs/plans/NUMERIC_FILTERS_FE_SPEC.md, numeric filters are NOT part of
 * this JSON body; they're flat `<key>_op`/`<key>_value` or
 * `<key>_op`/`<key>_min`/`<key>_max` query-string params. This field is
 * pre-flattened to the exact param names/values the caller should
 * `URLSearchParams.append` — see `compileNumericFilterQueryParams`.)
 */
export function convertToWildcardFormat(
  filters: Filters,
  availableFilters: {
    [filterType: string]: { name: string; id?: string | null; type?: string }[];
  }
): {
  filters: Record<string, string[] | "*">;
  exclude_filters?: Record<string, string[]>;
  numeric_query_params?: Record<string, string>;
  advanced_filters?: AdvancedFilterCard[];
} {
  const newFilters: Record<string, string[] | "*"> = {};
  const excludeFilters: Record<string, string[]> = {};
  const numericQueryParams: Record<string, string> = {};
  let advancedFilters: AdvancedFilterCard[] | undefined;

  Object.entries(filters).forEach(([filterType, selectedValues]) => {
    // Skip non-filter params
    if (
      filterType === "q" ||
      filterType === "limit" ||
      filterType === "offset" ||
      filterType === "perPage" ||
      filterType === "active_tag_view"
    ) {
      return;
    }

    // AP-1016 (Advanced Filters, Part 2): the whole set of criterion cards
    // lives under one reserved key as a JSON blob (see
    // `advancedFiltersUtils.ts`) — decode it into the nested `advanced_filters`
    // POST-body shape the backend expects. Unlike numeric filters, this is
    // NOT a query-param compiler: the wire shape is nested JSON and must go
    // in the POST body, never the URL, on every surface (see
    // `useAlternateRequest.ts`).
    if (filterType === ADVANCED_FILTERS_KEY) {
      const cards = decodeAdvancedFilterCards(selectedValues?.[0]);
      if (cards) {
        advancedFilters = compileAdvancedFilterCardsForRequest(cards);
      }
      return;
    }

    // AP-1016: a numeric filter's applied value is a single encoded
    // operator+value(s) string (see `encodeNumericFilterCriterion`), not a
    // chip-selection array — detected by attempting to decode it (rather
    // than relying on facet-type metadata, so this works the same way
    // `applyFiltersToURL` does for surfaces with no metadata on hand) and
    // routed to `numeric_query_params` instead of the wildcard/exclude
    // logic below, which only makes sense for chips.
    if (selectedValues?.length === 1) {
      const criterion = decodeNumericFilterCriterion(selectedValues[0]);
      if (criterion) {
        Object.assign(
          numericQueryParams,
          compileNumericFilterQueryParams(filterType, criterion)
        );
        return;
      }
    }

    const available = availableFilters[filterType];

    // Always omit empty arrays, regardless of metadata availability
    if (Array.isArray(selectedValues) && selectedValues.length === 0) {
      return;
    }

    if (!available || !Array.isArray(selectedValues)) {
      // No metadata or not an array - pass through as-is
      newFilters[filterType] = selectedValues;
      return;
    }

    const selectedCount = selectedValues.length;
    const totalCount = available.length;

    if (selectedCount === totalCount) {
      // All items selected - use wildcard
      newFilters[filterType] = "*";
    } else if (selectedCount > totalCount * 0.5) {
      // More than 50% selected - use exclude mode
      const selectedSet = new Set(selectedValues);
      const excluded = available
        .map((item) => item.name)
        .filter((name) => !selectedSet.has(name));

      if (excluded.length > 0) {
        newFilters[filterType] = "*";
        excludeFilters[filterType] = excluded;
      } else {
        // Fallback to normal mode if exclusion logic fails
        newFilters[filterType] = selectedValues;
      }
    } else {
      // Less than 50% selected - use normal include mode
      newFilters[filterType] = selectedValues;
    }
  });

  const result: {
    filters: Record<string, string[] | "*">;
    exclude_filters?: Record<string, string[]>;
    numeric_query_params?: Record<string, string>;
    advanced_filters?: AdvancedFilterCard[];
  } = {
    filters: newFilters,
  };

  if (Object.keys(excludeFilters).length > 0) {
    result.exclude_filters = excludeFilters;
  }

  if (Object.keys(numericQueryParams).length > 0) {
    result.numeric_query_params = numericQueryParams;
  }

  if (advancedFilters) {
    result.advanced_filters = advancedFilters;
  }

  return result;
}

export function convertToFiltersFormat(
  filters: IFilters,
  t: TFunction
): Filters {
  const newFilters: Filters = {};
  Object.entries(filters).forEach(([key, items]) => {
    if (key === "status") {
      newFilters[key] = items.map(({ name }) => getProductStatusValue(name, t));
    } else {
      // Mirrors chipsToStringArray, the live (non-preset) filtering path's
      // same rule: an id-keyed facet (e.g. `user`) is applied by id, not
      // its display name — a saved preset has to reapply the same value or
      // it won't match anything on restore.
      newFilters[key] = chipsToStringArray(
        items.map((item) => ({ ...item, id: item.id ?? undefined }))
      );
    }
  });
  return newFilters;
}
